import React, { FC, memo, useCallback, useEffect, useRef } from 'react';
import { Keyboard, KeyboardEvent, Platform } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { runOnJS } from 'react-native-worklets';
import { KeyboardSpacerProps } from './KeyboardSpacer.types';
import Box from '../Box/Box';

const IS_IOS = Platform.OS === 'ios';
const DEFAULT_TAB_BAR_HEIGHT = 49;

/**
 * It gives space below the element when keyboard is opened.
 * Could be used with KeyboardShift.
 */
const KeyboardSpacer: FC<KeyboardSpacerProps> = ({
  isInsideTabBar = false,
  isInsideSafeAreaView = true,
  additionalOffset = 0,
  onAnimationEnd,
  addSpace = true,
  timingConfig,
  ...rest
}) => {
  const insets = useSafeAreaInsets();
  const sharedKeyboardHeight = useSharedValue(0);
  const animationEndCallback = useRef(onAnimationEnd);

  const onAnimationEndHandler = useCallback(() => {
    animationEndCallback.current?.();
  }, []);

  const animatedKeyboardHeight = useAnimatedStyle(() => ({
    height: sharedKeyboardHeight.value,
  }));

  const handleKeyboardShow = useCallback(
    (event: KeyboardEvent) => {
      const bottomInset = Platform.OS === 'ios' ? insets.bottom : 0;
      const tabBarAdjustment = isInsideTabBar
        ? DEFAULT_TAB_BAR_HEIGHT
        : isInsideSafeAreaView
          ? bottomInset
          : 0;
      const keyboardHeight =
        event.endCoordinates.height - tabBarAdjustment + additionalOffset;

      sharedKeyboardHeight.value = withTiming(
        keyboardHeight,
        timingConfig,
        (isFinished) => {
          if (isFinished) {
            runOnJS(onAnimationEndHandler)();
          }
        },
      );
    },
    [
      isInsideTabBar,
      isInsideSafeAreaView,
      insets.bottom,
      additionalOffset,
      sharedKeyboardHeight,
      timingConfig,
      onAnimationEndHandler,
    ],
  );

  const handleKeyboardHide = useCallback(() => {
    sharedKeyboardHeight.value = withTiming(0, timingConfig);
  }, [sharedKeyboardHeight, timingConfig]);

  useEffect(() => {
    animationEndCallback.current = onAnimationEnd;
  }, [onAnimationEnd]);

  useFocusEffect(
    useCallback(() => {
      const showSubscription = Keyboard.addListener(
        IS_IOS ? 'keyboardWillShow' : 'keyboardDidShow',
        handleKeyboardShow,
      );
      const hideSubscription = Keyboard.addListener(
        IS_IOS ? 'keyboardWillHide' : 'keyboardDidHide',
        handleKeyboardHide,
      );

      return () => {
        sharedKeyboardHeight.value = 0;

        showSubscription.remove();
        hideSubscription.remove();
      };
    }, [handleKeyboardShow, handleKeyboardHide, sharedKeyboardHeight]),
  );

  return addSpace ? (
    <Box {...rest} animatedStyle={animatedKeyboardHeight} />
  ) : null;
};

export default memo(KeyboardSpacer);
